import type { TicketStatus, TicketUrgency, TodoState } from "@/lib/support";

export const STATUS_LABEL: Record<string, string> = {
  open: "offen",
  in_progress: "in Arbeit",
  waiting: "wartet",
  resolved: "gelöst",
  closed: "geschlossen",
};

export const URGENCY_LABEL: Record<string, string> = {
  low: "Niedrig",
  normal: "Normal",
  high: "Hoch",
  critical: "Super dringend",
};

export const TODO_LABEL: Record<string, string> = {
  eingang: "Eingang",
  unwichtig: "Unwichtig",
  wuensche: "Wünsche",
  umsetzbar: "Umsetzbar",
  wichtig: "Wichtig",
  in_umsetzung: "In Umsetzung",
  erledigt: "Erledigt",
};

export const URGENCY_RANK: Record<string, number> = {
  critical: 0,
  high: 1,
  normal: 2,
  low: 3,
};

export const TODO_RANK: Record<string, number> = {
  eingang: 0,
  wichtig: 1,
  in_umsetzung: 2,
  umsetzbar: 3,
  wuensche: 4,
  unwichtig: 5,
  erledigt: 6,
};

export const STALE_DAYS = 14;

export const PROGRAM_OPTIONS = [
  "Ernsthaus-App",
  "Hero",
  "Offpaper",
  "E-Mails",
  "Excel-Listen",
  "Sharepoint",
  "Sonstiges",
] as const;

export const QUICK_VIEWS: { key: TodoState | "all"; label: string }[] = [
  { key: "all", label: "Alle" },
  { key: "eingang", label: "Eingang" },
  { key: "wichtig", label: "Wichtig" },
  { key: "umsetzbar", label: "Umsetzbar" },
  { key: "in_umsetzung", label: "In Umsetzung" },
  { key: "wuensche", label: "Wünsche" },
  { key: "unwichtig", label: "Unwichtig" },
  { key: "erledigt", label: "Erledigt" },
];

/** Kanban-Spaltenreihenfolge (Arbeitsfluss) */
export const BOARD_COLUMNS: { key: TodoState; label: string }[] = [
  { key: "eingang", label: "Eingang" },
  { key: "wichtig", label: "Wichtig" },
  { key: "umsetzbar", label: "Umsetzbar" },
  { key: "in_umsetzung", label: "In Umsetzung" },
  { key: "wuensche", label: "Wünsche" },
  { key: "unwichtig", label: "Unwichtig" },
  { key: "erledigt", label: "Erledigt" },
];

export const STAT_FILTERS = [
  { key: "all", label: "Inbox", hint: "offen + in Arbeit" },
  { key: "critical", label: "Super dringend", hint: "urgency = critical" },
  { key: "mine", label: "Meine", hint: "mir zugewiesen" },
  { key: "unassigned", label: "Nicht zugewiesen", hint: "noch ohne Owner" },
  { key: "stale", label: "Älter als 14 Tage", hint: "ohne Aktivität" },
  { key: "ai_resolved", label: "KI: erledigt?", hint: "Vorschläge zur Bestätigung" },
] as const;

function mapLabel<T extends string>(map: Record<string, string>, value: T): string {
  return map[value] ?? value;
}

export const STATUS_OPTIONS: { value: TicketStatus; label: string }[] = [
  { value: "open", label: mapLabel(STATUS_LABEL, "open") },
  { value: "in_progress", label: mapLabel(STATUS_LABEL, "in_progress") },
  { value: "waiting", label: mapLabel(STATUS_LABEL, "waiting") },
  { value: "resolved", label: mapLabel(STATUS_LABEL, "resolved") },
  { value: "closed", label: mapLabel(STATUS_LABEL, "closed") },
];

export const URGENCY_OPTIONS: { value: TicketUrgency; label: string }[] = [
  { value: "critical", label: mapLabel(URGENCY_LABEL, "critical") },
  { value: "high", label: mapLabel(URGENCY_LABEL, "high") },
  { value: "normal", label: mapLabel(URGENCY_LABEL, "normal") },
  { value: "low", label: mapLabel(URGENCY_LABEL, "low") },
];

export const STATUS_USER_HINT: Record<string, string> = {
  open: "Dein Ticket ist eingegangen. Ich bearbeite es nach Dringlichkeit.",
  in_progress: "Ich arbeite gerade an deiner Anfrage.",
  waiting: "Ich habe eine Rückfrage. Antworte bitte unten im Verlauf.",
  resolved: "Das Problem sollte behoben sein. Bitte bestätige die Lösung oder öffne das Ticket erneut.",
  closed: "Dieses Ticket ist abgeschlossen. Bei einem neuen Problem erstelle einfach ein neues Ticket.",
};

export const URGENCY_USER_HINT: Record<string, string> = {
  low: "Keine Eile – kann in den nächsten Tagen bearbeitet werden.",
  normal: "Standard-Priorität für die reguläre Bearbeitung.",
  high: "Hohe Priorität – möglichst zeitnahe Bearbeitung.",
  critical: "Sehr dringend – wird mit höchster Priorität bearbeitet.",
};

export const USER_FILTER_CHIPS = [
  { key: "all", label: "Alle" },
  { key: "active", label: "Aktiv" },
  { key: "open", label: "Offen" },
  { key: "in_progress", label: "In Arbeit" },
  { key: "waiting", label: "Wartet auf mich" },
  { key: "resolved", label: "Gelöst" },
  { key: "closed", label: "Geschlossen" },
] as const;

export type UserFilterKey = (typeof USER_FILTER_CHIPS)[number]["key"];

export const TODO_FILTER_OPTIONS: { value: TodoState; label: string }[] = [
  { value: "eingang", label: mapLabel(TODO_LABEL, "eingang") },
  { value: "unwichtig", label: mapLabel(TODO_LABEL, "unwichtig") },
  { value: "wuensche", label: mapLabel(TODO_LABEL, "wuensche") },
  { value: "umsetzbar", label: mapLabel(TODO_LABEL, "umsetzbar") },
  { value: "wichtig", label: mapLabel(TODO_LABEL, "wichtig") },
  { value: "in_umsetzung", label: mapLabel(TODO_LABEL, "in_umsetzung") },
  { value: "erledigt", label: mapLabel(TODO_LABEL, "erledigt") },
];
